<script setup lang="ts" generic="T">
import { computed } from 'vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

type Option<T> = {
  label: string
  value: T
}

type Props<T> = {
  options: Array<Option<T>>
  placeholder?: string
  label?: string
  trackBy?: string
  searchable?: boolean
  closeOnSelect?: boolean
  showLabels?: boolean
  containerClass?: string
  class?: string
}

const model = defineModel<Option<T>>({ required: true });

const props = withDefaults(defineProps<Props<T>>(), {
  placeholder: 'Select option',
  label: 'label',
  trackBy: 'value',
  searchable: false,
  closeOnSelect: true,
  showLabels: false,
  containerClass: 'w-full',
  css: ''
});

const internalValue = computed({
  get: () => model.value,
  set: (value: Option<T>) => {
    model.value = value;
  }
});
</script>

<template>
  <div :class="props.containerClass">
    <Multiselect
      v-model="internalValue"
      :options="options"
      :searchable="searchable"
      :close-on-select="closeOnSelect"
      :show-labels="showLabels"
      :placeholder="placeholder"
      :label="label"
      :track-by="trackBy"
      :class="['multiselect-custom', props.class]"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </Multiselect>
  </div>
</template>

<style scoped>

:deep(.multiselect-custom .multiselect__tags) {
  border-radius: 9999px;
  border: 1px solid #dbeafe;
  background-color: #f9fafb;
  box-shadow: 0px 4px 6px 0px rgb(0 0 0 / 0.1);
  padding: 0.5rem 2.5rem 0 0.75rem;
}

:deep(.multiselect-custom .multiselect__single) {
  color: #1b5b90;
  font-weight: 600;
  margin-bottom: 0;
  background: #f9fafb ;
  line-height: 1.5rem;
}

:deep(.multiselect-custom .multiselect__option) {
  color: #1b5b90;
  padding: 0.75rem;
  min-height: auto;
}

:deep(.multiselect-custom .multiselect__option--highlight) {
  background: #dbeafe;
  color: #1b5b90;
}

:deep(.multiselect-custom .multiselect__option--selected) {
  background: #3b82f6;
  color: white;
  font-weight: 600;
}
</style>
